import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import PrimaryTabNavigation from "components/ui/PrimaryTabNavigation";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

import HeroSection from "./components/HeroSection";
import ModeCard from "./components/ModeCard";
import FeaturedGameCard from "./components/FeaturedGameCard";
import FilterBar from "./components/FilterBar";
import CapabilitySection from "./components/CapabilitySection";
import AIToolSection from "./components/AIToolSection";

const CREATION_MODES = [
{
  id: "text-to-game",
  title: "Text to Game",
  description: "Describe any game concept in plain English and AI generates a fully playable browser game with mechanics, art, and logic.",
  icon: "Gamepad2",
  gradient: "linear-gradient(135deg, #6366F1, #8B5CF6)",
  tags: ["Platformer", "Puzzle", "RPG"],
  cta: "Create Game",
  badge: "Popular",
  active: true
},
{
  id: "prompt-to-mini-game",
  title: "Mini Game Creator",
  description: "Generate quick, fun mini-games from a single sentence. Perfect for casual play and rapid prototyping.",
  icon: "Zap",
  gradient: "linear-gradient(135deg, #F59E0B, #EF4444)",
  tags: ["Quick", "Casual", "Fun"],
  cta: "Make Mini-Game",
  badge: "Fast",
  active: false
},
{
  id: "text-to-animation",
  title: "Animation Studio",
  description: "Create smooth character animations and cinematic scenes from text descriptions.",
  icon: "Film",
  gradient: "linear-gradient(135deg, #10B981, #06B6D4)",
  tags: ["Characters", "Cutscenes", "Motion"],
  cta: "Animate Now",
  badge: null,
  active: false
},
{
  id: "script-to-video",
  title: "Script to Video",
  description: "Transform scripts and storylines into cinematic video trailers with AI-generated visuals.",
  icon: "Video",
  gradient: "linear-gradient(135deg, #EC4899, #8B5CF6)",
  tags: ["Trailer", "Cinematic", "Story"],
  cta: "Create Video",
  badge: "New",
  active: false
},
{
  id: "music-to-video",
  title: "Music to Video",
  description: "Upload or describe game music and AI generates matching visual sequences and atmosphere videos.",
  icon: "Music",
  gradient: "linear-gradient(135deg, #06B6D4, #6366F1)",
  tags: ["Soundtrack", "Atmosphere", "Visual"],
  cta: "Generate Visual",
  badge: null,
  active: false
},
{
  id: "lip-sync",
  title: "Lip Sync Video",
  description: "Upload an image and audio to create talking character videos with realistic lip movement and facial animation.",
  icon: "Mic",
  gradient: "linear-gradient(135deg, #EC4899, #F43F5E)",
  tags: ["Avatar", "Character", "Talking"],
  cta: "Create Avatar",
  badge: "Hot",
  active: false
}];


const FEATURED_GAMES_INITIAL = [
{
  id: 1,
  title: "Neon Dungeon Crawler",
  prompt: "A cyberpunk dungeon crawler with neon lights, hacking mechanics, and robot enemies",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_162820546-1773033728918.png",
  thumbnailAlt: "Neon-lit cyberpunk dungeon corridor with glowing blue and purple lights and futuristic architecture",
  mode: "Text to Game",
  modeId: "text-to-game",
  likes: 1240,
  plays: 8900,
  createdAt: "2026-03-08",
  liked: false
},
{
  id: 2,
  title: "Space Bubble Pop",
  prompt: "A colorful space-themed bubble popping mini-game with alien characters",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_125897db6-1768039136141.png",
  thumbnailAlt: "Colorful space scene with vibrant nebula clouds, stars, and glowing cosmic bubbles in deep purple and blue",
  mode: "Mini-Game",
  modeId: "prompt-to-mini-game",
  likes: 876,
  plays: 5400,
  createdAt: "2026-03-07",
  liked: false
},
{
  id: 3,
  title: "Forest Guardian",
  prompt: "A peaceful forest platformer where you protect woodland creatures from pollution",
  thumbnail: "https://images.unsplash.com/photo-1731685131186-191e77db24e5",
  thumbnailAlt: "Lush green forest with tall ancient trees, dappled sunlight filtering through canopy, and mossy ground",
  mode: "Text to Game",
  modeId: "text-to-game",
  likes: 2100,
  plays: 14200,
  createdAt: "2026-03-06",
  liked: false
},
{
  id: 4,
  title: "Dragon Quest Intro",
  prompt: "Epic dragon quest game intro animation with medieval castle and fire breathing dragon",
  thumbnail: "https://images.unsplash.com/photo-1716840351622-9a46f0e4207b",
  thumbnailAlt: "Dramatic medieval castle silhouette against fiery orange sunset sky with dark storm clouds approaching",
  mode: "Animation",
  modeId: "text-to-animation",
  likes: 654,
  plays: 3200,
  createdAt: "2026-03-05",
  liked: false
},
{
  id: 5,
  title: "Pixel Racer Turbo",
  prompt: "A retro pixel art racing game with power-ups and multiple tracks",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_174fa0eb5-1773033727798.png",
  thumbnailAlt: "Retro-style pixel art racing track with colorful checkered patterns and neon speed lines",
  mode: "Mini-Game",
  modeId: "prompt-to-mini-game",
  likes: 1890,
  plays: 11000,
  createdAt: "2026-03-04",
  liked: false
},
{
  id: 6,
  title: "Ocean Adventure Trailer",
  prompt: "Underwater adventure game trailer with mermaids, treasure chests, and sea monsters",
  thumbnail: "https://images.unsplash.com/photo-1675180549778-beb03910a5fa",
  thumbnailAlt: "Crystal clear deep ocean water with vibrant coral reef, tropical fish, and shafts of sunlight penetrating surface",
  mode: "Script to Video",
  modeId: "script-to-video",
  likes: 432,
  plays: 2100,
  createdAt: "2026-03-03",
  liked: false
}];


const FILTER_OPTIONS = [
{ value: "all", label: "All" },
{ value: "text-to-game", label: "Games" },
{ value: "prompt-to-mini-game", label: "Mini-Games" },
{ value: "text-to-animation", label: "Animations" },
{ value: "script-to-video", label: "Videos" },
{ value: "popular", label: "Most Popular" }];


export default function HomeLandingScreen() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [games, setGames] = useState(FEATURED_GAMES_INITIAL);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredGames = useMemo(() => {
    let list = [...games];
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter((g) => g?.title?.toLowerCase()?.includes(q) || g?.prompt?.toLowerCase()?.includes(q));
    }
    if (activeFilter !== "all" && activeFilter !== "popular") {
      list = list?.filter((g) => g?.modeId === activeFilter);
    }
    if (activeFilter === "popular" || sortBy === "popular") {
      list?.sort((a, b) => b?.plays - a?.plays);
    } else {
      list?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
    }
    return list;
  }, [games, activeFilter, sortBy, searchQuery]);

  const handleLike = useCallback((gameId) => {
    setGames((prev) => prev?.map((g) =>
    g?.id === gameId ?
    { ...g, liked: !g?.liked, likes: g?.liked ? g?.likes - 1 : g?.likes + 1 } :
    g
    ));
  }, []);

  const handleModeSelect = (modeId) => {
    if (modeId === "lip-sync") {
      navigate("/creator-dashboard", { state: { mode: "lip-sync" } });
    } else {
      navigate("/creator-dashboard", { state: { mode: modeId } });
    }
  };

  const handlePlayGame = (game) => {
    navigate("/game-player-screen", { state: { game } });
  };

  const handleGetStarted = () => navigate("/creator-dashboard");
  const handleExplore = () => {
    document.getElementById("ai-tools")?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleSort = () => setSortBy((s) => s === "recent" ? "popular" : "recent");

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">

          {/* Hero */}
          <HeroSection onGetStarted={handleGetStarted} onExplore={handleExplore} />

          {/* AI Tool Sections */}
          <div id="ai-tools">
            <AIToolSection onToolSelect={handleModeSelect} />
          </div>

          {/* Creation Modes */}
          <section className="py-8 md:py-10 lg:py-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8">
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl" style={{ color: "var(--color-foreground)" }}>
                  Choose Your Creation Mode
                </h2>
                <p className="text-sm md:text-base mt-1" style={{ color: "var(--color-muted-foreground)" }}>
                  Six powerful AI tools to bring your ideas to life
                </p>
              </div>
              <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right" onClick={() => navigate("/creator-dashboard")}>
                Open Studio
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5">
              {CREATION_MODES?.map((mode) =>
              <ModeCard key={mode?.id} mode={mode} onSelect={handleModeSelect} />
              )}
            </div>
          </section>

          {/* Quick Start */}
          <section className="py-8 md:py-10">
            <div
              className="rounded-2xl p-6 md:p-8 lg:p-10 border"
              style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.06))", borderColor: "var(--color-border)" }}>
              
              <div className="text-center mb-6 md:mb-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl mb-2" style={{ color: "var(--color-foreground)" }}>
                  How It Works
                </h2>
                <p className="text-sm md:text-base" style={{ color: "var(--color-muted-foreground)" }}>
                  From idea to creation in under 30 seconds
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                { step: 1, icon: "MessageSquare", title: "Describe Your Idea", desc: "Type a prompt like 'a space shooter with power-ups'" },
                { step: 2, icon: "Cpu", title: "AI Generates It", desc: "Our AI builds mechanics, art, and logic in seconds" },
                { step: 3, icon: "Play", title: "Preview Instantly", desc: "Your creation is ready to preview in the browser" },
                { step: 4, icon: "Share2", title: "Share & Publish", desc: "Share with friends or publish to the community" }]?.
                map((s) =>
                <div key={s?.step} className="flex flex-col items-center text-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--color-primary), var(--color-secondary))" }}>
                        <Icon name={s?.icon} size={22} color="white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "var(--color-accent)", color: "#1a1a1a", fontFamily: "var(--font-data)" }}>
                        {s?.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm mb-1" style={{ color: "var(--color-foreground)" }}>{s?.title}</h3>
                      <p className="text-xs" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>{s?.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Featured Creations */}
          <section id="featured-games" className="py-8 md:py-10 lg:py-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl" style={{ color: "var(--color-foreground)" }}>
                  Featured Creations
                </h2>
                <p className="text-sm mt-1" style={{ color: "var(--color-muted-foreground)" }}>
                  Explore what the community has built
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="search"
                    placeholder="Search creations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-8 pr-3 py-2 text-xs rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-40"
                    style={{ background: "var(--color-card)", borderColor: "var(--color-border)", color: "var(--color-foreground)" }} />
                  
                </div>
                <button
                  onClick={toggleSort}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition-all hover:opacity-80"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                  
                  <Icon name="ArrowUpDown" size={12} />
                  {sortBy === "recent" ? "Recent" : "Popular"}
                </button>
              </div>
            </div>

            <FilterBar
              options={FILTER_OPTIONS}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter} />
            

            {filteredGames?.length === 0 ?
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>No creations found</p>
              </div> :

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredGames?.map((game) =>
              <FeaturedGameCard
                key={game?.id}
                game={game}
                onPlay={handlePlayGame}
                onLike={handleLike} />

              )}
              </div>
            }
          </section>

          {/* Capability Section */}
          <CapabilitySection />

          {/* CTA Section */}
          <section className="py-10 md:py-14">
            <div
              className="rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)" }}>
              
              <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 60%)" }} />
              <div className="relative z-10">
                <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-3">
                  Start Creating for Free
                </h2>
                <p className="text-white/80 text-sm md:text-base max-w-lg mx-auto mb-6">
                  Join 50,000+ creators building games, videos, animations, and music with AI.
                </p>
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold transition-all hover:scale-105 active:scale-95"
                  style={{ background: "white", color: "#6366F1", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", fontFamily: "var(--font-heading)" }}>
                  
                  <Icon name="Wand2" size={18} color="#6366F1" />
                  Create with AI — It's Free
                </button>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>);

}